import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  PAGE_SIZES,
} from 'src/shared/constants/table-settings-constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { Dispatch, useCallback, useMemo } from 'react'
import { Box, Chip, Grid2, IconButton, Stack, Tooltip } from '@mui/material'
import { DeleteIcon, EditIcon } from 'src/shared/icons/Icons'
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { useUserLocale } from 'src/user/UserContext'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { ConfigurationGridToolbar } from 'src/shared/grid/ConfigurationGridToolbar'

interface HelpAndInstructionsGridProps {
  dataSettings: ConfigurationGridDataSettings
  tableSettings: ConfigurationTablePageSortSettings
  setTableSettings: Dispatch<ConfigurationTablePageSortSettings>
  setDeleteConfirmationOpen: Dispatch<boolean>
  setHelpInstructionIdToBeDeleted: Dispatch<number>
}

export const HelpInstructionsConfigGrid = ({
  dataSettings,
  tableSettings,
  setTableSettings,
  setDeleteConfirmationOpen,
  setHelpInstructionIdToBeDeleted,
}: HelpAndInstructionsGridProps) => {
  const { rows, rowsCount, gridLoading } = dataSettings
  const { paginationModel } = tableSettings

  const { getMessage } = useMessageSource()
  const gridTranslations = useGridTranslateHook()
  const navigate = useDelayedNavigate()
  const locale = useUserLocale()

  const columns = useMemo(() => {
    return [
      {
        field: 'label_locale',
        headerName: `${getMessage('label.configuration.help.instructions.grid.label.locale')} ${locale.toUpperCase()}`,
        flex: 1,
        sortable: false,
        filterable: false,
      },
      {
        field: 'link_locale',
        headerName: `${getMessage('label.link')} ${locale.toUpperCase()}`,
        flex: 1,
        sortable: false,
        filterable: false,
      },
      {
        field: 'location_hints',
        headerName: getMessage('label.configuration.help.instructions.location.hints'),
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          const hints = params.value ?? []
          const MAX_VISIBLE_HINTS = 2
          const visible = hints.slice(0, MAX_VISIBLE_HINTS)
          const hiddenCount = Math.max(hints.length - MAX_VISIBLE_HINTS, 0)

          return (
            <Tooltip
              title={
                hints.length > 0 && (
                  <Box minWidth={150}>
                    {hints.map((hint: string, idx: number) => {
                      const listItemKey = `${hint}_${params.row.id}_${idx}`
                      return (
                        <Grid2 container key={listItemKey}>
                          <Grid2 size={1}>{`${idx + 1}.`}</Grid2>
                          <Grid2 size={11}>{hint}</Grid2>
                        </Grid2>
                      )
                    })}
                  </Box>
                )
              }
            >
              <Stack direction="row" height="100%" alignItems="center" overflow="hidden" gap={1}>
                {visible.map((hint: string, idx: number) => (
                  <Chip
                    key={`${hint}_${params.row.id}_${idx}`}
                    label={hint}
                    sx={{
                      width: 140,
                      maxWidth: 140,
                    }}
                  />
                ))}
                {hiddenCount > 0 && (
                  <Chip label={`+${hiddenCount}`} color="default" variant="outlined" sx={{ fontWeight: 500 }} />
                )}
              </Stack>
            </Tooltip>
          )
        },
      },
      {
        field: 'actions',
        headerName: getMessage('label.configuration.grid.actions'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.3,
        resizable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const onEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()

            const helpInstructionId = params.row.id
            navigate(ROUTES.HelpInstructionsConfigurationEdit.params({ helpInstructionId }))
          }
          const onDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()

            const helpInstructionId = params.row.id
            setHelpInstructionIdToBeDeleted(helpInstructionId)
            setDeleteConfirmationOpen(true)
          }

          return (
            <>
              <span>
                <IconButton onClick={onEditClick} color="primary">
                  <EditIcon />
                </IconButton>
              </span>
              <span>
                <IconButton onClick={onDeleteClick} color="primary">
                  <DeleteIcon />
                </IconButton>
              </span>
            </>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, locale, navigate, setDeleteConfirmationOpen, setHelpInstructionIdToBeDeleted])

  const handleCreateNewHelpLink = useCallback(() => {
    navigate(ROUTES.HelpInstructionsConfigurationCreate.params({}))
  }, [navigate])

  return (
    <DataGridContainer>
      <DataGridPro
        rows={rows}
        rowCount={rowsCount}
        columns={columns}
        loading={gridLoading}
        localeText={gridTranslations}
        disableColumnPinning
        disableRowSelectionOnClick
        disableColumnMenu
        pagination
        paginationModel={paginationModel}
        pageSizeOptions={PAGE_SIZES}
        filterMode="server"
        paginationMode="server"
        sortingMode="server"
        onPaginationModelChange={(newPagination) => {
          setTableSettings({
            ...tableSettings,
            paginationModel: newPagination,
          })
        }}
        sx={{
          boxShadow: 'none',
          border: 'none',
        }}
        slots={{
          toolbar: () => (
            <ConfigurationGridToolbar
              numResults={rowsCount}
              addButtonMessageKey="label.configuration.help.and.instructions.grid.create.button"
              onAddButtonClick={handleCreateNewHelpLink}
            />
          ),
        }}
      />
    </DataGridContainer>
  )
}
